import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageSquare, Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import AIDisclaimer from "../components/AIDisclaimer";
import api, { formatApiError } from "../lib/api";

const ALLOWED_EXT = ["pdf", "xlsx", "xls", "csv", "jpg", "jpeg", "png", "webp"];
const MAX_FILES = 3;
const MAX_BYTES = 20 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AIBoard() {
  const [consultants, setConsultants] = useState([]);
  const [active, setActive] = useState("buffett");
  const [question, setQuestion] = useState("");
  const [conversation, setConversation] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{id, filename, content_type, size, is_image}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/ai-board/consultants");
        setConsultants(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Nu pot incarca consultantii");
      }
      try {
        const { data: h } = await api.get("/api/ai-board/history");
        setHistory(h);
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  // Auto-grow textarea: 4-12 randuri.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22; // aprox px / rand
    const max = lineHeight * 12 + 16;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [question]);

  const uploadFile = async (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Tip nesuportat: .${ext}. Permise: ${ALLOWED_EXT.join(", ")}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} depaseste 20MB.`);
      return;
    }
    if (attachments.length >= MAX_FILES) {
      toast.error(`Maxim ${MAX_FILES} fisiere per intrebare.`);
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/api/ai-board/attachments", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload esuat");
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = "";
  };

  const onRemoveAttachment = async (att) => {
    setAttachments((a) => a.filter((x) => x.id !== att.id));
    try {
      await api.delete(`/api/ai-board/attachments/${att.id}`);
    } catch (_) {
      // ignore - server-side soft-delete failure nu blocheaza UI
    }
  };

  const onAsk = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q) return;
    setLoading(true);
    setConversation((c) => [
      ...c,
      { role: "user", text: q, ts: Date.now(), attachments: [...attachments] },
    ]);
    setQuestion("");
    const sendAttachmentIds = attachments.map((a) => a.id);
    setAttachments([]);
    try {
      const { data } = await api.post("/api/ai-board/ask", {
        consultant_id: active,
        question: q,
        session_id: sessionId,
        attachment_ids: sendAttachmentIds,
      });
      setSessionId(data.session_id);
      setConversation((c) => [
        ...c,
        { role: "ai", consultant: data.consultant_name, text: data.answer, ts: Date.now() },
      ]);
      const { data: h } = await api.get("/api/ai-board/history");
      setHistory(h);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Eroare LLM");
      setConversation((c) => [
        ...c,
        { role: "error", text: err?.response?.data?.detail || "Eroare", ts: Date.now() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      onAsk();
    }
  };

  const onSwitchConsultant = (id) => {
    if (id === active) return;
    setActive(id);
    setSessionId(null);
    setConversation([]);
  };

  const activeMeta = consultants.find((c) => c.id === active);

  return (
    <Layout title="AI Board">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr_280px]">
        <aside className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-white/40">Consultanti</h3>
          {consultants.map((c) => (
            <button
              key={c.id}
              data-testid={`ai-consultant-${c.id}`}
              type="button"
              onClick={() => onSwitchConsultant(c.id)}
              className={`block w-full rounded-lg border p-3 text-left transition ${
                c.id === active ? "border-accent-500 bg-accent-500/10" : "border-white/10 bg-ink-900 hover:border-white/30"
              }`}
            >
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-white/50">{c.domain}</div>
              <div className="mt-1 text-[11px] text-white/40">{c.focus}</div>
            </button>
          ))}
        </aside>

        <section className="rounded-lg border border-white/10 bg-ink-900 p-4 flex flex-col min-h-[60vh]">
          <div className="mb-3 border-b border-white/10 pb-2">
            <div className="text-xs uppercase tracking-widest text-white/40">Conversatie cu</div>
            <div data-testid="ai-active-name" className="text-base font-semibold">
              {activeMeta?.name || "..."}
            </div>
          </div>

          <div data-testid="ai-conversation" className="flex-1 space-y-3 overflow-y-auto pr-1">
            {conversation.length === 0 && (
              <p className="text-sm text-white/40">
                Pune o intrebare. Poti atasa pana la {MAX_FILES} fisiere (PDF, XLSX, XLS, CSV, JPG, PNG, WEBP) de max 20MB fiecare.
              </p>
            )}
            {conversation.map((msg, i) => (
              <div
                key={i}
                data-testid={`ai-msg-${msg.role}`}
                className={`rounded p-3 text-sm ${
                  msg.role === "user"
                    ? "border border-white/10 bg-ink-800"
                    : msg.role === "error"
                    ? "border border-red-500/40 bg-red-500/10 text-red-200"
                    : "border border-accent-500/30 bg-accent-500/5"
                }`}
              >
                {msg.role === "ai" && msg.consultant ? (
                  <div className="mb-1 text-xs uppercase tracking-widest text-accent-500">{msg.consultant}</div>
                ) : null}
                <div className="whitespace-pre-wrap">{msg.text}</div>
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {msg.attachments.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1 rounded border border-white/10 bg-ink-900 px-1.5 py-0.5 text-[11px] text-white/60"
                      >
                        {a.is_image ? <ImageIcon size={11} /> : <FileText size={11} />}
                        {a.filename}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-white/50">
                <Loader2 className="animate-spin" size={14} /> Genereaza raspuns...
              </div>
            )}
          </div>

          {attachments.length > 0 && (
            <div
              data-testid="ai-attachment-chips"
              className="mt-3 flex flex-wrap gap-2 border-t border-white/10 pt-3"
            >
              {attachments.map((att) => (
                <span
                  key={att.id}
                  data-testid={`ai-attachment-chip-${att.id}`}
                  className="inline-flex items-center gap-2 rounded border border-accent-500/30 bg-accent-500/10 px-2 py-1 text-xs text-white/80"
                >
                  {att.is_image ? <ImageIcon size={12} /> : <FileText size={12} />}
                  <span className="max-w-[160px] truncate">{att.filename}</span>
                  <span className="text-white/40">{formatBytes(att.size)}</span>
                  <button
                    type="button"
                    data-testid={`ai-attachment-remove-${att.id}`}
                    onClick={() => onRemoveAttachment(att)}
                    className="text-white/60 hover:text-white"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <form
            onSubmit={onAsk}
            className={`mt-3 flex gap-2 ${attachments.length > 0 ? "" : "border-t border-white/10 pt-3"}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              data-testid="ai-file-input"
              accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
              multiple
              hidden
              onChange={onPickFiles}
            />
            <button
              type="button"
              data-testid="ai-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading || attachments.length >= MAX_FILES}
              title="Ataseaza fisier (PDF/XLSX/XLS/CSV/JPG/PNG/WEBP, max 20MB)"
              className="self-start inline-flex items-center justify-center rounded border border-white/10 bg-ink-800 px-2 py-2 text-white/70 hover:border-accent-500 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Paperclip size={16} />}
            </button>
            <textarea
              ref={textareaRef}
              data-testid="ai-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Intreaba ${activeMeta?.name || ""}... (Ctrl+Enter pentru trimitere)`}
              rows={4}
              className="flex-1 resize-none rounded border border-white/10 bg-ink-800 px-3 py-2 text-sm outline-none focus:border-accent-500"
              style={{ minHeight: "104px", maxHeight: "280px" }}
              disabled={loading}
            />
            <button
              data-testid="ai-send-btn"
              type="submit"
              disabled={loading || !question.trim()}
              className="self-start inline-flex items-center gap-1.5 rounded bg-accent-500 px-3 py-2 text-sm font-semibold text-ink-950 disabled:opacity-50"
            >
              <Send size={14} /> Trimite
            </button>
          </form>

          <AIDisclaimer />
        </section>

        <aside>
          <h3 className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-white/40">
            <MessageSquare size={12} /> Istoric
          </h3>
          <div className="space-y-2">
            {history.length === 0 && <p className="text-xs text-white/40">Nicio intrebare anterioara.</p>}
            {history.slice(0, 30).map((h, i) => (
              <article
                key={i}
                data-testid={`ai-history-${i}`}
                className="rounded border border-white/10 bg-ink-900 p-2 text-xs"
              >
                <div className="text-[10px] uppercase text-accent-500">{h.consultant_id}</div>
                <div className="line-clamp-2 text-white/70">{h.question}</div>
                <div className="mt-1 line-clamp-3 text-white/40">{h.answer}</div>
              </article>
            ))}
          </div>
        </aside>
      </div>
    </Layout>
  );
}
